import classNames from 'classnames';
import dynamic from 'next/dynamic';
import React, { useMemo, useState } from 'react';

import { twMerge } from '@/core/tamagoshiTailwind/util/twMerge';
import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { Link } from '@/domains/core/routing/components/Link';
import { routes } from '@/domains/core/routing/routes/routes.fr';
import { Label } from '@/domains/core/tamagoshiTailwind/components/Label/Label';
import { Skeleton } from '@/domains/core/tamagoshiTailwind/components/Skeleton/Skeleton';
import {
  P,
  Typography,
} from '@/domains/core/tamagoshiTailwind/components/Typography/Typography';
import { ArrowRightIcon } from '@/domains/core/tamagoshiTailwind/icons/ArrowRightIcon';
import { SuperProMulticolorDarkSLogo } from '@/domains/core/tamagoshiTailwind/logos/superPro/SuperProMulticolorDarkSLogo';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { B2B_SUBSCRIPTIONS_RECRUITMENT_TOUCHPOINT_NO_MODAL_AB_TEST } from '@/domains/loyalty/modules/Subscriptions/constants';
import { useSubscriptionPlansQuery } from '@/domains/loyalty/modules/Subscriptions/generated/subscriptionPlans.query.generated';
import { getSubscriptionCheapestMonthlyPrice } from '@/domains/loyalty/modules/Subscriptions/RecruitmentTouchpoint/utils/getSubscriptionCheapestMonthlyPrice';
import { hasSales } from '@/domains/loyalty/modules/Subscriptions/RecruitmentTouchpoint/utils/hasSales';
import { StringAbTests } from '@/domains/productDiscovery/ProductPage/constants';

import { CLICK_RECRUITMENT_TOUCHPOINT_GTM_EVENT } from './gtmEvents';
import {
  buttonLabel,
  recruitmentTouchpointLabel,
  recruitmentTouchpointLabelWithVariablePrice,
  subscriptionLabel,
  subscriptionLabelWithSales,
} from './translations';

const DynamicRecruitmentModal = dynamic(
  async () =>
    (
      await import(
        '@/domains/loyalty/modules/Subscriptions/RecruitmentModal/RecruitmentModal'
      )
    ).RecruitmentModal,
  { ssr: false },
);

interface Props {
  className?: string;
}

const subscriptionRecruitmentTouchpointFormatter = () => {
  let i = 0;
  return {
    u: (chunks: React.ReactNode[]) => (
      <u
        className="underline decoration-accent-primary decoration-4
          underline-offset-[-1px] [text-decoration-skip-ink:none]"
        key={`subscription-highlight-${i++}`}
      >
        {chunks}
      </u>
    ),
  };
};

const subscriptionRecruitmentTouchpointWithVariablePriceFormatter = (
  price: string,
) => {
  let i = 0;
  return {
    u: (chunks: React.ReactNode[]) => (
      <u
        className="underline decoration-accent-primary decoration-4
          underline-offset-[-1px] [text-decoration-skip-ink:none]"
        key={`subscription-highlight-${i++}`}
      >
        {chunks}
      </u>
    ),
    price,
  };
};

const RecruitmentTouchpointContent: React.FC = () => {
  const [{ data, fetching, error }] = useSubscriptionPlansQuery({
    requestPolicy: 'cache-first',
  });

  const content = useMemo(
    () =>
      data?.subscriptionPlans && data.subscriptionPlans.length > 0 && !error
        ? recruitmentTouchpointLabelWithVariablePrice.format(
            subscriptionRecruitmentTouchpointWithVariablePriceFormatter(
              getSubscriptionCheapestMonthlyPrice(data.subscriptionPlans),
            ),
          )
        : recruitmentTouchpointLabel.format(
            subscriptionRecruitmentTouchpointFormatter(),
          ),
    [data, error],
  );

  if (fetching) {
    return (
      <Skeleton.Block
        width="100%"
        height={66}
        data-testid="recruitment-touchpoint-loading"
      />
    );
  }

  const inSales = hasSales(data?.subscriptionPlans || []);

  return (
    <>
      <div className="flex items-center gap-xs">
        <Label
          variant={inSales ? 'PromotionPrimary' : 'AccentPrimary'}
          text={inSales ? subscriptionLabelWithSales : subscriptionLabel}
          data-abtasty-id="super-pro-recruitment-touchpoint-label"
        />
        <SuperProMulticolorDarkSLogo className="h-m" />
      </div>
      <div className="flex flex-1 justify-between gap-s">
        <P
          className="shrink"
          scale="body2"
          weight="semibold"
          data-abtasty-id="super-pro-recruitment-touchpoint-text"
        >
          {content}
        </P>
        <div className="flex items-center gap-xs self-end">
          <Typography
            scale="body2"
            weight="semibold"
            data-abtasty-id="super-pro-recruitment-touchpoint-cta"
          >
            {buttonLabel}
          </Typography>
          <ArrowRightIcon />
        </div>
      </div>
    </>
  );
};

RecruitmentTouchpointContent.displayName = 'RecruitmentTouchpointContent';

const RecruitmentTouchpointWithModal = ({ className }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const onBannerClick = () => {
    setIsModalOpen(true);
    Gtm.push(CLICK_RECRUITMENT_TOUCHPOINT_GTM_EVENT);
  };
  return (
    <>
      <button
        tabIndex={0}
        onKeyUp={(e) => e.key === 'Enter' && onBannerClick()}
        onClick={onBannerClick}
        className={twMerge(
          `flex w-full cursor-pointer flex-col gap-xs rounded-container
          bg-accent-secondary p-m text-left text-foundation-primary`,
          className,
        )}
        data-abtasty-id="super-pro-recruitment-touchpoint"
      >
        <RecruitmentTouchpointContent />
      </button>
      {isModalOpen && (
        <DynamicRecruitmentModal onClose={() => setIsModalOpen(false)} />
      )}
    </>
  );
};

RecruitmentTouchpointWithModal.displayName = 'RecruitmentTouchpointWithModal';

const RecruitmentTouchpointWithoutModal = ({ className }: Props) => {
  const onBannerClick = () => {
    Gtm.push(CLICK_RECRUITMENT_TOUCHPOINT_GTM_EVENT);
  };
  return (
    <Link
      href={routes.subscriptionsPoc}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onBannerClick}
      className={twMerge(
        `flex w-full cursor-pointer flex-col gap-xs rounded-container
        bg-accent-secondary p-m text-foundation-primary`,
        className,
      )}
      data-abtasty-id="super-pro-recruitment-touchpoint"
    >
      <RecruitmentTouchpointContent />
    </Link>
  );
};

RecruitmentTouchpointWithoutModal.displayName =
  'RecruitmentTouchpointWithoutModal';

export const RecruitmentTouchpoint = ({ className }: Props) => {
  const [shouldHideModal, isProductPageRevampEnabled] = useBooleanFlags([
    B2B_SUBSCRIPTIONS_RECRUITMENT_TOUCHPOINT_NO_MODAL_AB_TEST,
    StringAbTests.ENABLE_PRODUCT_PAGE_REVAMP,
  ]);

  if (shouldHideModal) {
    return (
      <RecruitmentTouchpointWithoutModal
        className={classNames(
          className,
          !isProductPageRevampEnabled && 'border border-foundation-secondary',
        )}
      />
    );
  }

  return (
    <RecruitmentTouchpointWithModal
      className={classNames(
        className,
        !isProductPageRevampEnabled && 'border border-foundation-secondary',
      )}
    />
  );
};

RecruitmentTouchpoint.displayName = 'RecruitmentTouchpoint';
